import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { LanguageToggle } from "./LanguageToggle";

export const Header = () => {
    const { t } = useLang();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const links = [
        { to: "/", label: t.nav.home },
        { to: "/servicios", label: t.nav.services },
        { to: "/comercios", label: t.nav.commerce },
        { to: "/bancos", label: t.nav.banks },
        { to: "/personas", label: t.nav.people },
        { to: "/nosotros", label: t.nav.about },
        { to: "/contacto", label: t.nav.contact },
    ];

    return (
        <>
            <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[60] focus:bg-tinta focus:text-white focus:px-3 focus:py-2">
                {t.nav.skip}
            </a>
            <header
                data-testid="site-header"
                className={`sticky top-0 z-40 transition-all ${
                    scrolled
                        ? "bg-white/95 backdrop-blur-md shadow-[0_1px_0_var(--pt-linea)]"
                        : "bg-white"
                }`}
            >
                <div className="wrap flex h-[72px] items-center gap-6">
                    <Link
                        to="/"
                        aria-label="Pentomino, inicio"
                        data-testid="header-logo"
                        className="flex-none"
                    >
                        <img
                            src="/brand/logos/pentomino-horizontal-color.svg"
                            alt="Pentomino"
                            width="150"
                            height="19"
                            className="h-[22px] w-auto"
                        />
                    </Link>

                    <nav
                        aria-label="Principal"
                        className="ml-auto hidden items-center gap-6 lg:flex"
                        data-testid="header-nav-desktop"
                    >
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
                                className={({ isActive }) =>
                                    `relative text-[0.95rem] font-medium transition-colors ${
                                        isActive
                                            ? "text-coral-profundo"
                                            : "text-tinta hover:text-coral-profundo"
                                    }`
                                }
                            >
                                {({ isActive }) => (
                                    <span>
                                        {l.label}
                                        {isActive && (
                                            <span className="absolute -bottom-1.5 left-0 right-0 h-[2px] bg-coral" />
                                        )}
                                    </span>
                                )}
                            </NavLink>
                        ))}
                        <LanguageToggle />
                        <Link
                            to="/contacto"
                            data-testid="header-cta-btn"
                            className="pt-btn"
                        >
                            {t.nav.cta}
                        </Link>
                    </nav>

                    <button
                        type="button"
                        aria-expanded={open}
                        aria-controls="mobile-nav"
                        aria-label={t.nav.menu}
                        data-testid="header-menu-btn"
                        onClick={() => setOpen((v) => !v)}
                        className="ml-auto flex items-center justify-center rounded-full p-2 text-tinta lg:hidden"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>

                {/* Mobile nav */}
                {open && (
                    <div
                        id="mobile-nav"
                        data-testid="header-nav-mobile"
                        className="border-t border-linea bg-white lg:hidden"
                    >
                        <div className="wrap flex flex-col gap-1 py-4">
                            {links.map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    end={l.to === "/"}
                                    data-testid={`nav-link-mobile-${l.to.replace("/", "") || "home"}`}
                                    className={({ isActive }) =>
                                        `rounded-md px-3 py-2.5 text-[1.05rem] font-medium ${
                                            isActive
                                                ? "bg-niebla text-coral-profundo"
                                                : "text-tinta hover:bg-niebla"
                                        }`
                                    }
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                            <div className="mt-3 flex items-center justify-between gap-3 px-3">
                                <LanguageToggle />
                                <Link
                                    to="/contacto"
                                    className="pt-btn"
                                    data-testid="header-cta-btn-mobile"
                                >
                                    {t.nav.cta}
                                </Link>
                            </div>
                        </div>
                    </div>
                )}
            </header>
        </>
    );
};
